import React, { CSSProperties, isValidElement } from 'react'
import { useTheme, Tooltip, Typography, Box, SxProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import ReactSelect, {
  components,
  SingleValueProps,
  OptionProps,
  IndicatorsContainerProps,
  Theme as SelectTheme,
  StylesConfig,
  SingleValue,
  MenuPlacement,
  GroupBase,
  SelectComponentsConfig,
} from 'react-select'
import { truncate } from 'lodash'

import DropdownIndicator from './DropdownIndicator'
import ClearIndicator from './ClearIndicator'
import { getSelectStyles } from './styles'
import VirtualizedMenuList from './VirtualizedMenuList'
import { OptionTypeBase } from '../../Types/globalTypes'
import { getControlItem } from './get-control-item'

interface StyleProps {
  isMenuOpen?: boolean
  isMulti?: boolean
  minimal?: boolean
  isFloating?: boolean
  isFocused?: boolean
  fullWidth?: boolean
  disabled?: boolean
}

export type LabeledOption<T> = {
  label: string | React.ReactElement
  value: T
  isDisabled?: boolean
}

export type CustomStyles = { [key in keyof StylesConfig<OptionTypeBase, boolean>]: CSSProperties }

export const useSelectStyles = makeStyles<StyleProps>()((theme, props) => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    minWidth: 150,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  container: {
    minWidth: 150,
    width: props.fullWidth ? '100%' : undefined,
    'p + div': {
      paddingTop: 0,
      height: props.minimal ? 'max-content' : theme.shape.inputField.height,
      maxHeight: props.minimal ? 'max-content' : theme.shape.inputField.height,
      borderRadius: theme.shape.borderRadius,
    },
    p: {
      fontSize: theme.shape.inputField.fontSize,
    },
  },
}))

const useTooltipStyles = makeStyles()(theme => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  tooltip: {
    backgroundColor: theme.palette.error.main,
    top: -8,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  arrow: {
    color: theme.palette.error.main,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  highlight: {
    color: theme.palette.primary.main,
    fontSize: 16,
    padding: 8,
  },
  selectContainer: {
    width: '100%',
    color: theme.shape.select.textColor ? theme.shape.select.textColor : 'inherit',
  },
}))

export interface ShellSelectProps<T> {
  options: LabeledOption<T>[] | undefined
  value?: T | undefined
  onChange: (value: T) => void
  label?: string
  error?: { message: string } | string
  className?: string
  disabled?: boolean
  clearable?: boolean
  helperText?: string
  noOptionsMessage?: ({ inputValue }: { inputValue: string }) => string | null
  placeholder?: string
  minimal?: boolean
  hasBorder?: boolean
  dataQa?: string
  isSearchable?: boolean
  fullWidth?: boolean
  name?: string
  required?: boolean
  useTooltipErrorMessage?: boolean
  menuShouldBlockScroll?: boolean
  menuPortalTarget?: HTMLElement | null
  changeMenuPosition?: boolean
  customSelectStyles?: CustomStyles
  hasVirtualList?: boolean
  menuPlacement?: MenuPlacement
  customComponents?: SelectComponentsConfig<OptionTypeBase | undefined, boolean, GroupBase<OptionTypeBase>>
  isMulti?: boolean
  sx?: SxProps
}

export const FeatureRichSelect = <T,>({
  options,
  value,
  onChange,
  label,
  error,
  className,
  disabled,
  clearable = false,
  helperText,
  noOptionsMessage,
  placeholder,
  minimal = false,
  hasBorder = true,
  isSearchable = false,
  dataQa,
  fullWidth = true,
  name,
  required = false,
  useTooltipErrorMessage = true,
  menuShouldBlockScroll = true,
  menuPortalTarget = document.body,
  changeMenuPosition = false,
  customSelectStyles,
  hasVirtualList = false,
  menuPlacement,
  customComponents,
  isMulti = false,
  sx,
}: ShellSelectProps<T>): React.ReactElement => {
  const muiTheme = useTheme()
  const { classes } = useTooltipStyles()

  const { classes: inputClasses, cx } = useSelectStyles({ isMulti: false, minimal, fullWidth, disabled, isMenuOpen: false })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IndicatorsContainer = (props: IndicatorsContainerProps<any, any>): React.ReactElement => {
    return <components.IndicatorsContainer {...props}>{props.children}</components.IndicatorsContainer>
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Option = (props: OptionProps<any, any>): React.ReactElement => {
    const hasDescription = !!props.data.description
    const optionValue = props.data.value
    const optionDataQa =
      dataQa && (typeof optionValue === 'string' || typeof optionValue === 'number') ? `${dataQa}-option-${optionValue}` : undefined
    return (
      <components.Option {...props} innerProps={{ ...props.innerProps, ...(optionDataQa && { 'data-qa': optionDataQa }) }}>
        <Typography variant="caption">{props.data.label}</Typography>
        {hasDescription && <Typography variant="caption">{props.data.description}</Typography>}
      </components.Option>
    )
  }

  /** TODO: adjust single value design for disabled cases */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SingleValue = (props: SingleValueProps<any>): React.ReactElement => {
    if (isValidElement(props.data.label)) {
      /** ReactElement should be rendered as it is. */
      return <components.SingleValue {...props}>{props.data.label}</components.SingleValue>
    }

    return (
      <components.SingleValue {...props}>
        <Typography sx={{ opacity: disabled ? '50%' : '100%' }}>{truncate(props.data.label, { length: 60 })}</Typography>
      </components.SingleValue>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const MenuList = (props: any): React.ReactElement => {
    return <components.MenuList {...props}>{props.children}</components.MenuList>
  }

  const Select = (
    <ReactSelect
      required={required}
      value={options?.find(option => option.value === value) ?? null}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange={item => onChange((item as SingleValue<LabeledOption<any>>)?.value)}
      menuPortalTarget={menuPortalTarget}
      isSearchable={isSearchable}
      noOptionsMessage={noOptionsMessage}
      components={{
        DropdownIndicator,
        IndicatorSeparator: () => null,
        Control: getControlItem(label ?? '', required, dataQa, !!error, fullWidth, minimal),
        Option,
        SingleValue,
        IndicatorsContainer,
        MenuList: hasVirtualList ? VirtualizedMenuList : MenuList,
        ClearIndicator,
        ...customComponents,
      }}
      blurInputOnSelect
      styles={getSelectStyles(helperText ?? error, muiTheme, minimal, hasBorder, changeMenuPosition, customSelectStyles)}
      options={options}
      menuPlacement={menuPlacement ?? 'auto'}
      menuPosition="absolute"
      menuShouldBlockScroll={menuShouldBlockScroll}
      isClearable={clearable}
      placeholder={placeholder ?? ''}
      isMulti={isMulti}
      isDisabled={disabled}
      className={cx(inputClasses.container, className)}
      theme={(theme: SelectTheme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: muiTheme.palette.primary.main,
        },
      })}
    />
  )

  if (!useTooltipErrorMessage && helperText) {
    return (
      <Box className={cx({ [classes.selectContainer]: fullWidth })}>
        {Select}
        <Typography sx={{ paddingLeft: '14px', color: muiTheme.palette.error.main }} variant="caption">
          {helperText}
        </Typography>
      </Box>
    )
  }

  return (
    <Tooltip title={typeof error === 'string' ? error : error?.message || helperText || ''} open={!!error || !!helperText} arrow classes={classes}>
      <Box component={'span'} className={cx({ [classes.selectContainer]: fullWidth })} sx={sx}>
        {Select}
      </Box>
    </Tooltip>
  )
}
